import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';

import {
  createAgent,
  deleteAgent,
  listAgentLoginEvents,
  listAgentsAdmin,
  resetAgentPassword,
  setAgentDisabled,
  type AgentAdmin,
  type CreateAgentInput,
  type LoginEventsPage,
} from '@/features/agents/agents.api';

/** How often the roster refetches while the dialog is open, so the per-agent
 *  online lights stay roughly live. A modest poll (not a WS subscription) is
 *  the lower-blast-radius choice — no new QueryCache.subscribe (PR #20's
 *  render-loop) and it rides the same query the dialog already mounts. */
export const AGENTS_ADMIN_REFETCH_INTERVAL_MS = 15_000;

/** React Query key for the admin agent-management list. Distinct from the
 *  public directory's `['agents', 'list']` — the two endpoints return
 *  different shapes and have different visibility (this one is admin-only). */
export const agentsAdminQueryKey = ['agents', 'admin'] as const;

interface UseAgentsAdminOptions {
  /** Gate on the ManageAgentsDialog being open. The query MUST NOT fire for
   *  non-admins or while the dialog is closed — the caller passes the
   *  dialog's `open` flag straight through. */
  enabled: boolean;
}

/**
 * React Query hook over `GET /api/v1/agents/admin` (admin only).
 *
 *   - `enabled` — the caller gates the fetch on the dialog being open; a
 *     closed dialog (or a non-admin who can never open it) never issues the
 *     request.
 *   - `staleTime: 0` — agent management is a rare, deliberate action; a fresh
 *     fetch on every dialog open is the right trade-off so the admin never
 *     acts on a stale roster.
 *   - `retry: 1` — one quiet retry on a transient blip.
 */
export function useAgentsAdmin(
  options: UseAgentsAdminOptions,
): UseQueryResult<AgentAdmin[], Error> {
  return useQuery<AgentAdmin[], Error>({
    queryKey: agentsAdminQueryKey,
    queryFn: listAgentsAdmin,
    enabled: options.enabled,
    staleTime: 0,
    retry: 1,
    // Poll only while the dialog is open so the online lights stay live; `false`
    // when closed stops the timer entirely (no background polling).
    refetchInterval: options.enabled ? AGENTS_ADMIN_REFETCH_INTERVAL_MS : false,
  });
}

interface SetAgentDisabledVariables {
  agentId: string;
  disabled: boolean;
}

/**
 * Extract the human-readable failure message for an agent-mutation error.
 *
 * The Hub's guard-rails answer with a specific `detail` string ("cannot
 * disable your own account", "an agent with this email already exists", "agent
 * has sent messages and cannot be deleted; disable it instead", …) — surface
 * it verbatim; anything else (network blip, 5xx) falls back to a generic
 * message. Shared by every agent mutation below.
 */
function backendDetailMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string' && detail.length > 0) {
      return detail;
    }
  }
  return '操作失败，请重试';
}

/**
 * Mutation over `PATCH /api/v1/agents/{id}` — enable/disable an agent.
 *
 * Deliberately NOT optimistic: disabling force-logs-out a colleague, so the
 * pill should only flip once the server confirms. On success the returned
 * row is written into the admin-list cache in place (no flicker), a success
 * toast confirms the action, then the key is invalidated so the next open
 * refetches server truth. On error the backend `detail` surfaces via a toast
 * (same sonner channel as the rest of the console).
 */
export function useSetAgentDisabled(): UseMutationResult<
  AgentAdmin,
  unknown,
  SetAgentDisabledVariables
> {
  const queryClient = useQueryClient();

  return useMutation<AgentAdmin, unknown, SetAgentDisabledVariables>({
    mutationFn: ({ agentId, disabled }) => setAgentDisabled(agentId, disabled),

    onSuccess: (updated) => {
      queryClient.setQueryData<AgentAdmin[]>(agentsAdminQueryKey, (rows) =>
        rows?.map((row) => (row.id === updated.id ? updated : row)),
      );
      toast.success(
        updated.disabled ? `已禁用 ${updated.display_name}` : `已恢复 ${updated.display_name}`,
      );
      void queryClient.invalidateQueries({ queryKey: agentsAdminQueryKey });
    },

    onError: (error) => {
      toast.error(backendDetailMessage(error));
    },
  });
}

/**
 * Mutation over `POST /api/v1/agents` — create a console agent.
 *
 * On success the new row is written into the admin-list cache, a toast
 * confirms, and the key is invalidated so the roster refetches server truth.
 * On error the backend `detail` (duplicate email → "an agent with this email
 * already exists") surfaces via toast.
 */
export function useCreateAgent(): UseMutationResult<
  AgentAdmin,
  unknown,
  CreateAgentInput
> {
  const queryClient = useQueryClient();

  return useMutation<AgentAdmin, unknown, CreateAgentInput>({
    mutationFn: (input) => createAgent(input),

    onSuccess: (created) => {
      queryClient.setQueryData<AgentAdmin[]>(agentsAdminQueryKey, (rows) =>
        rows ? [...rows, created] : [created],
      );
      toast.success(`已新增坐席 ${created.display_name}`);
      void queryClient.invalidateQueries({ queryKey: agentsAdminQueryKey });
    },

    onError: (error) => {
      toast.error(backendDetailMessage(error));
    },
  });
}

/**
 * Mutation over `DELETE /api/v1/agents/{id}` — hard-delete an agent with no
 * history. NOT optimistic (a 409-has-history must not vanish the row): on
 * success the row is removed from cache, a toast confirms, and the key is
 * invalidated. On error the backend `detail` surfaces via toast — a 409
 * steers the admin to disable instead.
 */
export function useDeleteAgent(): UseMutationResult<
  { agentId: string; display_name: string },
  unknown,
  { agentId: string; display_name: string }
> {
  const queryClient = useQueryClient();

  return useMutation<
    { agentId: string; display_name: string },
    unknown,
    { agentId: string; display_name: string }
  >({
    mutationFn: async (vars) => {
      await deleteAgent(vars.agentId);
      return vars;
    },

    onSuccess: ({ agentId, display_name }) => {
      queryClient.setQueryData<AgentAdmin[]>(agentsAdminQueryKey, (rows) =>
        rows?.filter((row) => row.id !== agentId),
      );
      toast.success(`已删除坐席 ${display_name}`);
      void queryClient.invalidateQueries({ queryKey: agentsAdminQueryKey });
    },

    onError: (error) => {
      // A 409 with a has-history detail is the common case — surface the
      // backend's Chinese-steer verbatim isn't possible (it's English), so map
      // the 409 to the operator-facing sentence; anything else uses the detail.
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        toast.error('该坐席有历史记录,只能禁用');
        return;
      }
      toast.error(backendDetailMessage(error));
    },
  });
}

interface ResetAgentPasswordVariables {
  agentId: string;
  display_name: string;
  password: string;
}

/**
 * Mutation over `PATCH /api/v1/agents/{id}/password` — reset a password. No
 * cache write (the row shape is unchanged); a success toast names the agent,
 * and errors surface the backend `detail`.
 */
export function useResetAgentPassword(): UseMutationResult<
  ResetAgentPasswordVariables,
  unknown,
  ResetAgentPasswordVariables
> {
  return useMutation<ResetAgentPasswordVariables, unknown, ResetAgentPasswordVariables>({
    mutationFn: async (vars) => {
      await resetAgentPassword(vars.agentId, vars.password);
      return vars;
    },

    onSuccess: ({ display_name }) => {
      toast.success(`已重置 ${display_name} 的密码`);
    },

    onError: (error) => {
      toast.error(backendDetailMessage(error));
    },
  });
}

/** React Query key for one agent's login-event history. */
export function agentLoginEventsQueryKey(agentId: string): readonly unknown[] {
  return ['agents', 'login-events', agentId] as const;
}

/**
 * React Query hook over `GET /api/v1/agents/{id}/login-events` (admin only).
 * Gated on `enabled` (the login-log sub-view being visible for this agent) so
 * it never fires from the roster view. Newest first, first page only.
 */
export function useAgentLoginEvents(
  agentId: string | null,
  options: { enabled: boolean },
): UseQueryResult<LoginEventsPage, Error> {
  return useQuery<LoginEventsPage, Error>({
    queryKey: agentLoginEventsQueryKey(agentId ?? '__none__'),
    queryFn: () => listAgentLoginEvents(agentId as string),
    enabled: options.enabled && agentId !== null,
    staleTime: 0,
    retry: 1,
  });
}
